import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye } from "lucide-react";

const CATEGORIES = [
    "Direito Imobiliário",
    "Direito do Trabalho",
    "Direito Civil",
    "Direito Penal",
    "Direito Administrativo",
    "Direito Fiscal e Tributário",
    "Direito Comercial e Financeiro",
    "Imigração e Nacionalidade",
    "Direito Empresarial e Patrimonial",
    "Contratos",
    "Actos Notariais",
    "Outros",
];

const slugify = (s) =>
    s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "")
        .substring(0, 90);

const emptyForm = {
    slug: "",
    title: "",
    excerpt: "",
    category: CATEGORIES[0],
    author: "",
    image: "",
    content_html: "",
    date_iso: "",
    notify_subscribers: true,
};

const ArticleForm = () => {
    const { slug } = useParams();
    const isEdit = Boolean(slug);
    const { authAxios } = useAuth();
    const nav = useNavigate();

    const [form, setForm] = useState(emptyForm);
    const [collaborators, setCollaborators] = useState([]);
    const [slugTouched, setSlugTouched] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(false);

    // Load collaborators + existing article
    useEffect(() => {
        authAxios
            .get("/admin/collaborators")
            .then((r) => {
                setCollaborators(r.data);
                setForm((f) => ({ ...f, author: f.author || r.data[0] || "" }));
            })
            .catch(() => {});
        if (isEdit) {
            authAxios
                .get("/admin/articles")
                .then((r) => {
                    const a = r.data.find((x) => x.slug === slug);
                    if (!a) {
                        toast.error("Artigo não encontrado");
                        nav("/admin");
                        return;
                    }
                    setForm({
                        slug: a.slug,
                        title: a.title,
                        excerpt: a.excerpt,
                        category: a.category,
                        author: a.author,
                        image: a.image,
                        content_html: a.content_html,
                        date_iso: a.date_iso,
                        notify_subscribers: false, // edits don't notify by default
                    });
                    setSlugTouched(true);
                })
                .catch(() => toast.error("Erro ao carregar artigo"))
                .finally(() => setLoading(false));
        }
    }, [slug, isEdit, nav, authAxios]);

    const set = (k) => (e) => {
        const v = e.target.value;
        setForm((f) => {
            const next = { ...f, [k]: v };
            if (k === "title" && !slugTouched && !isEdit) {
                next.slug = slugify(v);
            }
            return next;
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title || !form.excerpt || !form.content_html) {
            toast.error("Preencha título, resumo e conteúdo.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                slug: form.slug || slugify(form.title),
                title: form.title,
                excerpt: form.excerpt,
                category: form.category,
                author: form.author,
                image:
                    form.image ||
                    "/images/wp/2026/05/Ana-Cristina-Advocacia-Portugal-Direito-Advogados-2.jpg",
                content_html: form.content_html,
                date_iso: form.date_iso || null,
                notify_subscribers: !isEdit && form.notify_subscribers,
            };
            if (isEdit) {
                await authAxios.put(`/admin/articles/${slug}`, payload);
                toast.success("Artigo atualizado com sucesso");
            } else {
                await authAxios.post("/admin/articles", payload);
                toast.success("Artigo publicado com sucesso");
            }
            nav("/admin");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(
                typeof detail === "string" ? detail : "Erro ao guardar",
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center text-[color:var(--acd-muted)] p-10">
                A carregar...
            </div>
        );
    }

    return (
        <div data-testid="article-form">
            <div className="flex items-center justify-between mb-6">
                <Link
                    to="/admin"
                    className="inline-flex items-center gap-2 text-sm text-[color:var(--acd-muted)] hover:text-[var(--acd-navy-900)] transition"
                >
                    <ArrowLeft size={14} /> Voltar
                </Link>
                <button
                    onClick={() => setPreview((p) => !p)}
                    className="acd-btn-outline text-xs"
                    type="button"
                >
                    <Eye size={14} /> {preview ? "Ocultar" : "Ver"} pré-visualização
                </button>
            </div>

            <h1 className="text-2xl font-bold text-[var(--acd-navy-900)] mb-6">
                {isEdit ? "Editar artigo" : "Novo artigo"}
            </h1>

            <form
                onSubmit={submit}
                className="bg-white p-6 rounded-xl border border-gray-100 space-y-4"
            >
                <div>
                    <label className="acd-label">Título *</label>
                    <input
                        className="acd-input"
                        required
                        value={form.title}
                        onChange={set("title")}
                        data-testid="form-title"
                        placeholder="Ex: Novo regime de arrendamento em 2026"
                    />
                </div>
                <div>
                    <label className="acd-label">
                        Slug (URL) — gerado automaticamente, editável
                    </label>
                    <input
                        className="acd-input font-mono text-sm"
                        required
                        value={form.slug}
                        onChange={(e) => {
                            setSlugTouched(true);
                            setForm((f) => ({
                                ...f,
                                slug: slugify(e.target.value),
                            }));
                        }}
                        data-testid="form-slug"
                    />
                    <p className="text-xs text-[color:var(--acd-muted)] mt-1">
                        URL final: /artigos/{form.slug || "..."}
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="acd-label">Categoria *</label>
                        <select
                            className="acd-select"
                            value={form.category}
                            onChange={set("category")}
                            data-testid="form-category"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="acd-label">
                            Autor (colaborador) *
                        </label>
                        <select
                            className="acd-select"
                            value={form.author}
                            onChange={set("author")}
                            data-testid="form-author"
                        >
                            {collaborators.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div>
                    <label className="acd-label">Resumo (excerto) *</label>
                    <textarea
                        className="acd-textarea"
                        rows={2}
                        required
                        value={form.excerpt}
                        onChange={set("excerpt")}
                        data-testid="form-excerpt"
                        placeholder="Frase curta que aparece na listagem de artigos e nas partilhas em redes sociais"
                    />
                </div>
                <div>
                    <label className="acd-label">Imagem de capa (URL)</label>
                    <input
                        className="acd-input"
                        value={form.image}
                        onChange={set("image")}
                        placeholder="https://... (deixe vazio para usar imagem por defeito)"
                        data-testid="form-image"
                    />
                    {form.image && (
                        <img
                            src={form.image}
                            alt=""
                            className="mt-2 w-40 h-24 object-cover rounded-lg border border-gray-200"
                            onError={(e) => {
                                e.target.style.display = "none";
                            }}
                        />
                    )}
                </div>
                <div>
                    <label className="acd-label">Conteúdo (HTML) *</label>
                    <p className="text-xs text-[color:var(--acd-muted)] mb-2">
                        Use tags HTML simples: &lt;p&gt;, &lt;h2&gt;, &lt;h3&gt;,
                        &lt;ul&gt;/&lt;li&gt;, &lt;strong&gt;, &lt;a href=&quot;&quot;&gt;,
                        &lt;blockquote&gt;.
                    </p>
                    <textarea
                        className="acd-textarea font-mono text-sm"
                        rows={20}
                        required
                        value={form.content_html}
                        onChange={set("content_html")}
                        data-testid="form-content"
                        placeholder={"<p>Introdução do artigo...</p>\n<h2>Primeiro subtítulo</h2>\n<p>Parágrafo do conteúdo.</p>"}
                    />
                </div>

                {preview && form.content_html && (
                    <div className="border border-gray-200 rounded-lg p-6 bg-white">
                        <h3 className="text-sm font-bold text-[color:var(--acd-muted)] uppercase tracking-widest mb-4">
                            Pré-visualização
                        </h3>
                        <h2 className="text-2xl font-bold text-[var(--acd-navy-900)] mb-2">
                            {form.title}
                        </h2>
                        <p className="text-sm text-[color:var(--acd-muted)] italic mb-4">
                            {form.excerpt}
                        </p>
                        <div
                            className="acd-prose"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(form.content_html),
                            }}
                        />
                    </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-gray-100">
                    <button
                        type="submit"
                        disabled={saving}
                        className="acd-btn-gradient"
                        data-testid="form-submit"
                    >
                        <Save size={16} />
                        {saving
                            ? "A guardar..."
                            : isEdit
                              ? "Guardar alterações"
                              : "Publicar artigo"}
                    </button>
                    <Link to="/admin" className="acd-btn-outline">
                        Cancelar
                    </Link>
                </div>

                {!isEdit && (
                    <label className="flex items-start gap-3 text-sm text-gray-700 bg-[var(--acd-bg-soft)] p-4 rounded-lg -mt-2">
                        <input
                            type="checkbox"
                            checked={form.notify_subscribers}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    notify_subscribers: e.target.checked,
                                }))
                            }
                            className="mt-1 h-4 w-4 accent-[var(--acd-blue-600)]"
                            data-testid="form-notify"
                        />
                        <span>
                            <strong>Notificar subscritores da newsletter por
                            email</strong> assim que o artigo for publicado.
                            Cada subscritor recebe o artigo com um link de
                            leitura e opção de cancelar subscrição.
                        </span>
                    </label>
                )}
            </form>
        </div>
    );
};

export default ArticleForm;
